import { useState } from 'react';
import { useAllTags, useTagCategories } from '../../graphql/queries';
import type { Tag, TagCategory } from '../../graphql/types';
import { resolveTagCategoryName } from '../../lib/tagCategoryName';
import { TagBadge } from './TagBadge';
import './TagPicker.css';

const UNCATEGORIZED_GROUP_KEY = '__uncategorized__';
const UNCATEGORIZED_GROUP_LABEL = 'Uncategorized';

export interface TagPickerProps {
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
  label?: string;
  disabled?: boolean;
  /**
   * DOM id for the combobox input. Defaults to `tag-picker-input` for
   * backward compatibility, but callers rendering more than one `TagPicker`
   * on the same page (e.g. include + exclude, issue #489) must pass distinct
   * ids to avoid duplicate-id / mismatched `htmlFor` accessibility bugs.
   */
  id?: string;
  /**
   * Tag categories used to convey each option's category (issue #714) —
   * grouped section headers plus a secondary label on every option, and
   * included in the text filter alongside the tag name.
   *
   * Optional **override**: `TagPicker` self-fetches tag categories via
   * `useTagCategories` (issue #794) so every instance shows category context
   * with no per-caller wiring. Pass this prop only when a caller already has
   * categories loaded (e.g. to avoid a duplicate query) or explicitly wants
   * category-less rendering by passing `[]`.
   */
  categories?: TagCategory[];
  /**
   * Opt-in single-select exclusion (issue #794): when true, for any category
   * where `allowMultiple === false` that already has one of its tags in
   * `selectedTagIds`, the category's *other* tags are removed from the add
   * options — the slot is "locked" until the user removes the assigned chip
   * (freeing the category) and then adds the replacement. Already-selected
   * tags are unaffected (still shown as chips); multi-select categories and
   * uncategorized tags are never locked. Default `false` preserves prior
   * behavior for existing callers.
   */
  enforceSingleSelect?: boolean;
  /**
   * Restrict the option list (and selected-chip lookup) to tags belonging to
   * this single category. Used by the combined Service Tags & Custom Fields
   * section (issue #746) to render one inline picker per category row —
   * grouping is skipped since there is only one category in scope.
   */
  categoryId?: string;
  /**
   * Exclude tags whose `categoryId` is in this set from the option list.
   * Used for the "other tags" general-catalog affordance so tags already
   * governed by a category-specific row aren't offered twice (issue #746).
   * Ignored when `categoryId` is set.
   */
  excludeCategoryIds?: string[];
  /**
   * Suppress the visible `<label>` element while still exposing `label` as
   * the input's `aria-label`. Used when a category row's own `.detail-label`
   * already names the field, so repeating the name above the picker would be
   * redundant (issue #746).
   */
  hideLabel?: boolean;
}

export interface TagOptionGroup {
  key: string;
  label: string;
  tags: Tag[];
}

/** Buckets tags by category, preserving `categories` order; unmatched tags go last. */
export function groupTagsByCategory(tags: Tag[], categories: TagCategory[]): TagOptionGroup[] {
  const groups: TagOptionGroup[] = categories.map((category) => ({
    key: category.entityId,
    label: category.name,
    tags: [],
  }));
  const groupById = new Map(groups.map((group) => [group.key, group]));
  const uncategorized: TagOptionGroup = {
    key: UNCATEGORIZED_GROUP_KEY,
    label: UNCATEGORIZED_GROUP_LABEL,
    tags: [],
  };

  for (const tag of tags) {
    const group = groupById.get(tag.categoryId);
    (group ?? uncategorized).tags.push(tag);
  }

  return [...groups, uncategorized].filter((group) => group.tags.length > 0);
}

export function TagPicker({
  selectedTagIds,
  onChange,
  label = 'Tags',
  disabled = false,
  id = 'tag-picker-input',
  categories: categoriesOverride,
  categoryId,
  excludeCategoryIds,
  hideLabel = false,
  enforceSingleSelect = false,
}: TagPickerProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Full catalog, not a single capped page (issue #1352) — this picker's own
  // text filter below runs client-side against `allTags`, so it needs every
  // tag to search against, matching the pattern already used by
  // `useAllTechnologyDomains` (#764) and by `useAllTags`'s other callers.
  const { tags: allTagsRaw, loading } = useAllTags();
  const { data: fetchedCategoriesData } = useTagCategories();

  const categories = categoriesOverride ?? fetchedCategoriesData?.tagCategories ?? [];

  // A single-category scope (one picker per Custom Fields row) never groups —
  // there is nothing to group by. Grouping only applies to the general,
  // multi-category picker.
  const hasCategories = !categoryId && categories.length > 0;
  const categoryNameById = new Map(
    categories.map((category) => [category.entityId, category.name])
  );

  const allTags: Tag[] = allTagsRaw.filter((tag) => {
    if (categoryId) return tag.categoryId === categoryId;
    if (excludeCategoryIds && excludeCategoryIds.includes(tag.categoryId)) return false;
    return true;
  });

  // Single-select exclusion (issue #794): a category is "locked" once one of
  // its tags is already selected, so its remaining tags drop out of the add
  // options until the assigned chip is removed.
  const selectedTagCategoryIds = new Set(
    allTags.filter((tag) => selectedTagIds.includes(tag.entityId)).map((tag) => tag.categoryId)
  );
  const lockedCategoryIds = enforceSingleSelect
    ? new Set(
        categories
          .filter(
            (category) => !category.allowMultiple && selectedTagCategoryIds.has(category.entityId)
          )
          .map((category) => category.entityId)
      )
    : new Set<string>();

  const filteredTags = allTags.filter((tag) => {
    if (selectedTagIds.includes(tag.entityId)) {
      return false;
    }
    if (lockedCategoryIds.has(tag.categoryId)) {
      return false;
    }
    const query = inputValue.toLowerCase();
    const nameMatches = tag.name.toLowerCase().includes(query);
    const categoryName = categoryNameById.get(tag.categoryId);
    const categoryMatches = categoryName ? categoryName.toLowerCase().includes(query) : false;
    return nameMatches || categoryMatches;
  });
  const selectedTags = allTags.filter((tag) => selectedTagIds.includes(tag.entityId));
  const optionGroups = hasCategories ? groupTagsByCategory(filteredTags, categories) : [];

  function handleSelect(tag: Tag): void {
    onChange([...selectedTagIds, tag.entityId]);
    setInputValue('');
    setIsOpen(false);
  }

  function handleRemove(tagId: string): void {
    onChange(selectedTagIds.filter((id) => id !== tagId));
  }

  function renderOption(tag: Tag): JSX.Element {
    const categoryLabel = hasCategories
      ? (categoryNameById.get(tag.categoryId) ?? UNCATEGORIZED_GROUP_LABEL)
      : undefined;
    return (
      <li
        key={tag.entityId}
        role="option"
        aria-selected="false"
        className="tag-picker__option"
        onMouseDown={() => handleSelect(tag)}
      >
        <span className="tag-picker__option-row">
          <span
            className="tag-picker__option-swatch"
            style={{ backgroundColor: tag.color || '#6B7280' }}
          />
          <span className="tag-picker__option-name">{tag.name}</span>
        </span>
        {categoryLabel && (
          <span className="tag-picker__option-category" data-testid="tag-picker-option-category">
            {categoryLabel}
          </span>
        )}
      </li>
    );
  }

  return (
    <div className="tag-picker">
      {label && !hideLabel && (
        <label className="tag-picker__label" htmlFor={id}>
          {label}
        </label>
      )}

      <div className="tag-picker__selected">
        {selectedTags.map((tag) => (
          <TagBadge
            key={tag.entityId}
            tag={tag}
            onRemove={handleRemove}
            categoryName={
              hasCategories ? resolveTagCategoryName(tag.categoryId, categories) : undefined
            }
          />
        ))}
      </div>

      <div className="tag-picker__combobox">
        <input
          id={id}
          role="combobox"
          aria-label={label}
          aria-expanded={isOpen}
          aria-autocomplete="list"
          autoComplete="off"
          className="tag-picker__input"
          placeholder={loading ? 'Loading tags…' : 'Search tags…'}
          value={inputValue}
          disabled={disabled || loading}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        />

        {isOpen && (
          <ul className="tag-picker__options" role="listbox">
            {filteredTags.length === 0 ? (
              <li className="tag-picker__no-options" role="option" aria-disabled="true">
                No tags available
              </li>
            ) : hasCategories ? (
              optionGroups.flatMap((group) => [
                <li
                  key={`${group.key}-header`}
                  role="presentation"
                  className="tag-picker__group-header"
                  data-testid="tag-picker-group-header"
                >
                  {group.label}
                </li>,
                ...group.tags.map((tag) => renderOption(tag)),
              ])
            ) : (
              filteredTags.map((tag) => renderOption(tag))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
